import type { ReactNode } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import type { UserRole } from '../types/api'

interface RequireRoleProps {
  role: UserRole | UserRole[]
  children?: ReactNode
}

/**
 * Guard de ruta por rol. Si no hay sesión o el rol no matchea, redirige a
 * /login con `?from=<path actual>` para volver post-login.
 *
 * Uso:
 *   <Route element={<RequireRole role="super_admin" />}>
 *     <Route path="/app/admin/..." element={<AdminLayout />} />
 *   </Route>
 */
export function RequireRole({ role, children }: RequireRoleProps) {
  const user = useAuthStore((s) => s.user)
  const token = useAuthStore((s) => s.token)
  const hydrated = useAuthStore((s) => s.hydrated)
  const location = useLocation()

  if (!hydrated) {
    return null
  }

  const allowed = Array.isArray(role) ? role : [role]
  const authorized = !!token && !!user && allowed.includes(user.role)

  if (!authorized) {
    const from = encodeURIComponent(`${location.pathname}${location.search}`)
    return <Navigate to={`/app/login?from=${from}`} replace />
  }

  return children ? <>{children}</> : <Outlet />
}
